import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Properties
export const getProperties = () => axios.get(`${API}/properties`);
export const createProperty = (data) => axios.post(`${API}/properties`, data);
export const updateProperty = (id, data) => axios.put(`${API}/properties/${id}`, data);
export const deleteProperty = (id) => axios.delete(`${API}/properties/${id}`);

// Rooms
export const getRooms = (propertyId) => axios.get(`${API}/rooms`, { params: { property_id: propertyId } });
export const createRoom = (data) => axios.post(`${API}/rooms`, data);
export const updateRoom = (id, data) => axios.put(`${API}/rooms/${id}`, data);
export const deleteRoom = (id) => axios.delete(`${API}/rooms/${id}`);

// Guests
export const getDashboardAlerts = () => axios.get(`${API}/reports/dashboard-alerts`);
export const getGuestLedgerDues = (id) => axios.get(`${API}/guests/${id}/ledger-dues`);
export const getGuests = (params) => axios.get(`${API}/guests`, { params });
export const getGuest = (id) => axios.get(`${API}/guests/${id}`);
export const createGuest = (data) => axios.post(`${API}/guests`, data);

// Guest KYC (photo + ID documents) — private object storage
export const getGuestKycStatus = (guestId) => axios.get(`${API}/guests/${guestId}/kyc`);
export const uploadGuestKyc = (guestId, docType, blob) => {
  const fd = new FormData();
  fd.append('file', blob, `${docType}.jpg`);
  return axios.post(`${API}/guests/${guestId}/kyc/${docType}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const getGuestKycImageBlob = (guestId, docType, version) => axios.get(`${API}/guests/${guestId}/kyc/${docType}/image${version ? `?v=${encodeURIComponent(version)}` : ''}`, { responseType: 'blob' });
export const deleteGuestKyc = (guestId, docType) => axios.delete(`${API}/guests/${guestId}/kyc/${docType}`);
export const getGuestDetailsPdf = (guestId) => axios.get(`${API}/guests/${guestId}/details-pdf`, { responseType: 'blob' });
export const updateGuest = (id, data) => axios.put(`${API}/guests/${id}`, data);
export const deleteGuest = (id) => axios.delete(`${API}/guests/${id}`);

// Guest Actions
export const joinBookedGuest = (data) => axios.post(`${API}/guests/join-booked`, data);
export const giveNotice = (data) => axios.post(`${API}/guests/notice`, data);
export const withdrawNotice = (data) => axios.post(`${API}/guests/withdraw-notice`, data);
export const reverseLeave = (data) => axios.post(`${API}/guests/reverse-leave`, data);
export const processLeave = (data) => axios.post(`${API}/guests/leave`, data);
export const shiftGuest = (data) => axios.post(`${API}/guests/shift`, data);
export const changeDueDate = (data) => axios.post(`${API}/guests/change-due-date`, data);
export const recordAdvanceRent = (data) => axios.post(`${API}/guests/advance-rent`, data);

// Payments/Receipts
export const getPayments = (params) => axios.get(`${API}/payments`, { params });
export const recordPayment = (data) => axios.post(`${API}/payments`, data);
export const updatePayment = (id, data) => axios.put(`${API}/payments/${id}`, null, { params: data });
export const deletePayment = (id) => axios.delete(`${API}/payments/${id}`);
export const exportReceiptsReport = (params) => axios.get(`${API}/export/receipts`, { params, responseType: 'blob' });

// Electricity
export const getElectricityReadings = (params) => axios.get(`${API}/electricity/readings`, { params });
export const recordElectricityReading = (data) => axios.post(`${API}/electricity/reading`, data);
export const updateElectricityReading = (id, data) => axios.put(`${API}/electricity/reading/${id}`, data);
export const deleteElectricityReading = (id) => axios.delete(`${API}/electricity/reading/${id}`);

// Daily Hisab
export const getDailyHisab = (params) => axios.get(`${API}/daily-hisab`, { params });
export const recordDailyHisab = (data) => axios.post(`${API}/daily-hisab`, data);
export const updateDailyHisab = (id, data) => axios.put(`${API}/daily-hisab/${id}`, data);
export const deleteDailyHisab = (id) => axios.delete(`${API}/daily-hisab/${id}`);

// Expenses
export const getExpenses = (params) => axios.get(`${API}/expenses`, { params });
export const recordExpense = (data) => axios.post(`${API}/expenses`, data);
export const updateExpense = (id, data) => axios.put(`${API}/expenses/${id}`, data);
export const deleteExpense = (id) => axios.delete(`${API}/expenses/${id}`);
export const processDepositReturn = (data) => axios.post(`${API}/guests/deposit-return`, data);

// Reports
export const getSummary = (propertyId) => axios.get(`${API}/reports/summary`, { params: { property_id: propertyId } });
export const getMonthlyReport = (month) => axios.get(`${API}/reports/monthly`, { params: { month } });
export const getCollectionReport = (month) => axios.get(`${API}/reports/collection`, { params: { month } });
export const getGuestLeaveReport = (month) => axios.get(`${API}/reports/guest-leave`, { params: { month } });
export const getOutstandingReport = () => axios.get(`${API}/reports/outstanding`);

// Excel Export Functions
export const exportCollectionReport = async (month) => {
  const response = await axios.get(`${API}/export/collection`, { 
    params: { month }, 
    responseType: 'blob' 
  });
  return response;
};

export const exportExpensesReport = async (params) => {
  const response = await axios.get(`${API}/export/expenses`, { 
    params, 
    responseType: 'blob' 
  });
  return response;
};

export const exportOutstandingReport = async () => {
  const response = await axios.get(`${API}/export/outstanding`, { 
    responseType: 'blob' 
  });
  return response;
};

export const exportMonthlySummary = async (month) => {
  const response = await axios.get(`${API}/export/monthly-summary`, { 
    params: { month }, 
    responseType: 'blob' 
  });
  return response;
};

// User Management (Admin only)
export const getUsers = () => axios.get(`${API}/users`);
export const createUser = (data) => axios.post(`${API}/users`, data);
export const updateUser = (id, data) => axios.put(`${API}/users/${id}`, data);
export const deleteUser = (id) => axios.delete(`${API}/users/${id}`);
export const getRoles = () => axios.get(`${API}/auth/roles`);

// Analytics & Reminders
export const getAnalytics = (months = 6) => axios.get(`${API}/reports/analytics`, { params: { months } });
export const getReceivables = (propertyId) => axios.get(`${API}/reports/receivables`, { params: { property_id: propertyId } });
export const getOpeningBalance = (params) => axios.get(`${API}/reports/opening-balance`, { params });
export const getPaymentReminders = () => axios.get(`${API}/reports/payment-reminders`);
export const getReceiptsPayments = (params) => axios.get(`${API}/reports/receipts-payments`, { params });
export const exportReceiptsPaymentsExcel = (params) => axios.get(`${API}/reports/receipts-payments/excel`, { params, responseType: 'blob' });
export const exportReceiptsPaymentsPdf = (params) => axios.get(`${API}/reports/receipts-payments/pdf`, { params, responseType: 'blob' });

// Excel Import Functions
export const importReceipts = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/import/receipts`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
};

export const downloadReceiptsImportTemplate = () => 
  axios.get(`${API}/import/receipts/template`, { responseType: 'blob' });

export const importExpenses = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/import/expenses`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
};

export const downloadExpensesImportTemplate = () => 
  axios.get(`${API}/import/expenses/template`, { responseType: 'blob' });

// Rent Management
export const calculateRentPreview = (monthlyRent, joiningDate, alignToFirst = false) => 
  axios.get(`${API}/rent/calculate-preview`, { 
    params: { monthly_rent: monthlyRent, joining_date: joiningDate, align_to_first: alignToFirst } 
  });

export const getRentDues = (params) => axios.get(`${API}/rent/dues`, { params });
export const updateRentDue = (id, data) => axios.put(`${API}/rent/dues/${id}`, data);
export const generateMonthlyRentDues = (monthYear) => 
  axios.post(`${API}/rent/generate-monthly-dues`, null, { params: { month_year: monthYear } });
export const getGuestRentCycleInfo = (guestId) => axios.get(`${API}/rent/guest-cycle-info/${guestId}`);
export const migrateExistingGuests = () => axios.post(`${API}/rent/migrate-existing-guests`);

// Data Sync
export const syncBeds = () => axios.post(`${API}/sync/beds`);

// Excel Export Functions
export const exportElectricityReport = (month) => 
  axios.get(`${API}/export/electricity`, { params: { month }, responseType: 'blob' });

export const exportGuestList = (params = {}) =>
  axios.get(`${API}/export/guest-list`, { params, responseType: 'blob' });

export const exportGuestLeaveReport = () => 
  axios.get(`${API}/export/guest-leave`, { responseType: 'blob' });

// Guest Import
export const importGuests = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/import/guests`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
};

export const downloadGuestImportTemplate = () => 
  axios.get(`${API}/import/guest-template`, { responseType: 'blob' });

// Electricity Import
export const importElectricity = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await axios.post(`${API}/import/electricity`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return response;
};

export const downloadElectricityTemplate = () => 
  axios.get(`${API}/import/electricity-template`, { responseType: 'blob' });

// Guest Ledger Register
export const getGuestLedgerRegister = () => axios.get(`${API}/ledger/guest-register`);
export const getLedgerAudit = () => axios.get(`${API}/reports/ledger-audit`);
export const getDailyHisabSummary = (month) => axios.get(`${API}/ledger/daily-hisab-summary`, { params: { month } });
export const calculateDaywiseRentPreview = (monthlyRent, joiningDate, periodEndDate) => 
  axios.get(`${API}/ledger/calculate-rent-preview`, { 
    params: { monthly_rent: monthlyRent, joining_date: joiningDate, period_end_date: periodEndDate } 
  });

// Guest Statement
export const getGuestStatementList = (propertyId, status) => 
  axios.get(`${API}/reports/guest-statement-list`, { params: { property_id: propertyId, status } });
export const getGuestStatement = (guestId) => axios.get(`${API}/reports/guest-statement/${guestId}`);
export const exportGuestStatementExcel = (guestId) => 
  axios.get(`${API}/export/guest-statement/excel/${guestId}`, { responseType: 'blob' });
export const exportGuestStatementPdf = (guestId) => 
  axios.get(`${API}/export/guest-statement/pdf/${guestId}`, { responseType: 'blob' });

// Invoice/Receipt Generation
export const generateInvoice = (data) => 
  axios.post(`${API}/invoice/generate`, data, { responseType: 'blob' });
export const generateReceipt = (data) => 
  axios.post(`${API}/receipt/generate`, data, { responseType: 'blob' });

// Export Guest Ledger
export const exportGuestLedgerReport = () => 
  axios.get(`${API}/export/guest-ledger`, { responseType: 'blob' });

// ============ ADVANCE BOOKINGS ============
export const getBookings = (params) => axios.get(`${API}/bookings`, { params });
export const getBooking = (id) => axios.get(`${API}/bookings/${id}`);
export const getBookingStats = () => axios.get(`${API}/bookings/stats`);
export const createBooking = (data) => axios.post(`${API}/bookings`, data);
export const updateBooking = (id, data) => axios.put(`${API}/bookings/${id}`, data);
export const collectBookingDeposit = (data) => axios.post(`${API}/bookings/collect-deposit`, data);
export const convertBookingToGuest = (data) => axios.post(`${API}/bookings/convert-to-guest`, data);
export const cancelBooking = (id) => axios.post(`${API}/bookings/${id}/cancel`);
export const retrieveBooking = (id) => axios.post(`${API}/bookings/${id}/retrieve`);
export const deleteBooking = (id) => axios.delete(`${API}/bookings/${id}`);
export const getBookingPolicy = () => axios.get(`${API}/organisation/booking-policy`);
export const updateBookingPolicy = (booking_hold_days) => axios.put(`${API}/organisation/booking-policy`, { booking_hold_days });

// Late Penalty Rate (per-organisation setting)
export const getPenaltyRate = () => axios.get(`${API}/organisation/penalty-rate`);
export const updatePenaltyRate = (late_penalty_rate) => axios.put(`${API}/organisation/penalty-rate`, { late_penalty_rate });

// Other income categories (per-organisation, editable)
export const getOtherCategories = () => axios.get(`${API}/organisation/other-categories`);
export const updateOtherCategories = (categories) => axios.put(`${API}/organisation/other-categories`, { categories });
export const getPaymentCategories = () => axios.get(`${API}/organisation/payment-categories`);
export const updatePaymentCategories = (categories) => axios.put(`${API}/organisation/payment-categories`, { categories });

// Organisation Settings (business details, opening balances, Guest ID prefix)
export const getOrganisationSettings = () => axios.get(`${API}/organisation/settings`);
export const updateOrganisationSettings = (data) => axios.put(`${API}/organisation/settings`, data);
export const exportAllData = () => axios.get(`${API}/admin/export-data`, { responseType: 'blob' });
export const restoreAllData = (formData) => axios.post(`${API}/admin/restore-data`, formData);

// Ledger Adjustments (Penalty / Waiver)
export const getGuestAdjustments = (guestId) => axios.get(`${API}/guests/${guestId}/adjustments`);
export const getPenaltySuggestion = (guestId) => axios.get(`${API}/guests/${guestId}/penalty-suggestion`);
export const createAdjustment = (data) => axios.post(`${API}/adjustments`, data);
export const updateAdjustment = (id, data) => axios.put(`${API}/adjustments/${id}`, data);
export const deleteAdjustment = (id) => axios.delete(`${API}/adjustments/${id}`);
